import { useQuery } from '@tanstack/react-query';
import type { CatalogSnapshot } from '@idas/shared';
import { api } from '../../lib/api';
import { dashboardGate } from '../../lib/query-gate';

/**
 * The values a filter can actually take.
 *
 * A text filter on `project_name` is really a choice from a fixed list, and
 * asking someone to type one exactly — spelling, spacing and case — is asking
 * them to guess. The column is looked up in the catalog to find the table it
 * belongs to, then its distinct values are read and offered as a dropdown.
 *
 * Everything about this is best-effort. If the column cannot be placed, or the
 * table is denied by policy, or the read fails, the caller falls back to a
 * plain text box: a filter that still works is better than an error where a
 * convenience was meant to be.
 */

/** Enough to choose from; small enough not to be a data export. */
const MAX_OPTIONS = 200;

/** Only worth looking up for things people pick, not things they measure. */
const LOOKUP_TYPES = /char|text|enum/i;

export interface ColumnOptions {
  options: string[];
  loading: boolean;
  /** Where the values came from, for the control's title. */
  source: string | null;
}

/**
 * Finds the table a bare column name belongs to.
 *
 * A qualifier in the SQL is usually an alias (`p.project_name`), so it cannot
 * be trusted as a table name — but when it does match one, it settles the
 * question. Otherwise the search is by column name, preferring a readable
 * table over a denied one, and refusing to guess when several tables match
 * equally well.
 */
function locate(
  snapshot: CatalogSnapshot | undefined,
  column: string,
): { database: string; table: string; column: string; dataType: string } | null {
  if (!snapshot) return null;

  const parts = column.split('.');
  const bare = parts[parts.length - 1]!;
  const qualifier = parts.length > 1 ? parts[parts.length - 2]! : null;

  const matches: Array<{ database: string; table: string; column: string; dataType: string }> = [];

  for (const database of snapshot.databases) {
    for (const table of database.tables) {
      if (table.denied) continue;
      const found = table.columns.find((c) => c.name.toLowerCase() === bare.toLowerCase());
      // A column policy hides is not one to enumerate.
      if (!found || found.restriction === 'DENIED') continue;
      matches.push({
        database: database.name,
        table: table.name,
        column: found.name,
        dataType: found.dataType,
      });
    }
  }

  if (!matches.length) return null;

  // An exact table match on the qualifier wins outright.
  if (qualifier) {
    const exact = matches.find((m) => m.table.toLowerCase() === qualifier.toLowerCase());
    if (exact) return exact;
  }

  // `project_name` living in `projects` is the common convention and a good
  // signal when several tables carry the same column.
  const stem = bare.replace(/_?(name|id|code|title)$/i, '');
  if (stem) {
    const byStem = matches.filter((m) => m.table.toLowerCase().startsWith(stem.toLowerCase()));
    if (byStem.length === 1) return byStem[0]!;
  }

  // Exactly one candidate is an answer; several is a guess, and a dropdown
  // listing the wrong table's values is worse than a text box.
  return matches.length === 1 ? matches[0]! : null;
}

export function useColumnOptions(
  dataSourceId: string | undefined,
  column: string | null,
  enabled: boolean,
): ColumnOptions {
  const catalog = useQuery({
    queryKey: ['catalog', dataSourceId],
    queryFn: () => api.catalog.snapshot(dataSourceId!),
    enabled: Boolean(dataSourceId) && enabled,
    staleTime: 10 * 60_000,
  });

  const target = enabled && column ? locate(catalog.data, column) : null;
  const lookupable = target !== null && LOOKUP_TYPES.test(target.dataType);

  const values = useQuery({
    queryKey: ['column-values', dataSourceId, target?.database, target?.table, target?.column],
    enabled: Boolean(dataSourceId) && lookupable,
    staleTime: 10 * 60_000,
    retry: false,
    queryFn: () =>
      dashboardGate(() =>
        api.query.execute({
          dataSourceId: dataSourceId!,
          // Identifiers come from the catalog, and are quoted anyway. The
          // statement is an ordinary SELECT, so it passes the same guard,
          // policy and audit as anything else — a lookup is not a side door.
          sql:
            `SELECT DISTINCT \`${target!.column}\`\n` +
            `  FROM \`${target!.database}\`.\`${target!.table}\`\n` +
            ` WHERE \`${target!.column}\` IS NOT NULL AND \`${target!.column}\` <> ''\n` +
            ` ORDER BY \`${target!.column}\`\n` +
            ` LIMIT ${MAX_OPTIONS}`,
          clientQueryId: `opts-${target!.table}-${target!.column}`,
          maxRows: MAX_OPTIONS,
        }),
      ),
  });

  const options =
    values.data?.status === 'SUCCESS'
      ? values.data.rows
          .map((row) => (row[0] === null ? '' : String(row[0])))
          .filter((v) => v !== '')
      : [];

  return {
    options,
    loading: catalog.isLoading || values.isLoading,
    source: target ? `${target.table}.${target.column}` : null,
  };
}
